import { getCompanyBureauUrl } from 'app/_utils/helpers';
import { encodeUrlPath } from 'app/_utils/safe-markdown';
import {
  ADMIN_NAVIGATION_PAGES,
  AdminNavigationPage,
  AdminPageLinkMode,
} from './admin-navigation-pages';

function toToolsPath(toolsLink: string): string {
  if (toolsLink.startsWith('/tools')) {
    return toolsLink;
  }
  return `/tools${toolsLink.startsWith('/') ? toolsLink : `/${toolsLink}`}`;
}

function appendQueryParams(url: string, params?: Record<string, string>): string {
  if (!params || Object.keys(params).length === 0) {
    return url;
  }
  const search = new URLSearchParams(params).toString();
  return url.includes('?') ? `${url}&${search}` : `${url}?${search}`;
}

/**
 * Build an absolute admin tools URL matching tools-menu routing.
 */
export function buildAdminPageUrl(
  origin: string,
  companyName: string,
  bureauName: string,
  page: Pick<AdminNavigationPage, 'link' | 'linkMode'>,
  companyId?: string,
): string {
  const base = origin.replace(/\/$/, '');
  const toolsPath = toToolsPath(page.link);
  const linkMode: AdminPageLinkMode = page.linkMode || 'bureau';

  if (linkMode === 'global') {
    const queryParams: Record<string, string> = {};
    if (companyId) {
      queryParams.company_id = companyId;
    }
    return appendQueryParams(`${base}${toolsPath}`, queryParams);
  }

  if (linkMode === 'globalCompanyPath' && companyId && companyName && bureauName) {
    const routerPath = getCompanyBureauUrl(toolsPath, companyName, bureauName);
    return `${base}${encodeUrlPath(routerPath)}`;
  }

  if (linkMode === 'globalCompanyPath') {
    return `${base}${toolsPath}`;
  }

  const routerPath = getCompanyBureauUrl(toolsPath, companyName, bureauName);
  return `${base}${encodeUrlPath(routerPath)}`;
}

/**
 * Context fields merged into Bedrock agent promptOverrides for admin how-to links.
 */
export function buildAdminNavigationPromptContext(
  origin: string,
  companyName: string,
  bureauName: string,
  companyId?: string,
): Record<string, unknown> | undefined {
  if (!companyName || !bureauName) {
    return undefined;
  }

  const webAppOrigin = origin.replace(/\/$/, '');

  return {
    web_app_origin: webAppOrigin,
    company_id: companyId || undefined,
    admin_navigation_pages: ADMIN_NAVIGATION_PAGES.map((page) => ({
      title: page.title,
      keywords: page.keywords,
      path: toToolsPath(page.link),
      link_mode: page.linkMode || 'bureau',
      url: buildAdminPageUrl(webAppOrigin, companyName, bureauName, page, companyId),
    })),
  };
}
